/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Orchestrator post-process that applies version gating to Monaco-shape
 * completion items.
 *
 * Native dialect resolvers can apply gating earlier via
 * `decorateCandidates` + `rankCandidates`, but the legacy LSP resolver
 * produces Monaco items directly without going through the ranker. This
 * post-process is the single place that handles BOTH paths uniformly:
 *   - For each Monaco item it derives the canonical name from the label,
 *     looks up the catalog, and folds in the badge + tags + sortText demote.
 *   - Items that didn't go through the ranker (legacy LSP) get the version
 *     metadata for free; items that already did (native resolvers) are
 *     idempotent because the ranker stamps the same fields and the lookup
 *     is keyed on label, not on already-applied state.
 *
 * Pure module — Monaco namespace flows in via the caller's `monaco` param so
 * the orchestrator stays process-agnostic.
 */

import type { CompletionCandidate } from '@/core/engines';

import {
  decorateCandidates,
  type VersionFilterMode,
} from '@/features/intellisense/versionGating/decorate';
import type { CompatContext } from '@/features/intellisense/versionGating/resolveCompat';

export type VersionGatingPostProcessHint = {
  readonly compat: CompatContext;
  readonly mode: VersionFilterMode;
};

export function applyVersionGatingPostProcess(
  suggestions: readonly any[],
  hint: VersionGatingPostProcessHint | undefined,
  monaco: any,
): readonly any[] {
  if (!hint || hint.mode === 'off' || suggestions.length === 0) return suggestions;

  const monacoKeywordKind = monaco?.languages?.CompletionItemKind?.Keyword;
  const monacoFunctionKind = monaco?.languages?.CompletionItemKind?.Function;
  const monacoMethodKind = monaco?.languages?.CompletionItemKind?.Method;
  const out: any[] = [];

  for (const item of suggestions) {
    if (!isCatalogEligible(item, monacoKeywordKind, monacoFunctionKind, monacoMethodKind)) {
      out.push(item);
      continue;
    }
    const labelText = readLabelText(item);
    if (!labelText) {
      out.push(item);
      continue;
    }
    const synthetic: CompletionCandidate = {
      label: labelText,
      insertText: typeof item.insertText === 'string' ? item.insertText : labelText,
      isSnippet: false,
      kind: 'keyword',
      tier: 7,
    };
    const decorated = decorateCandidates([synthetic], hint.compat, hint.mode);
    if (decorated.length === 0) continue; // strict mode dropped it
    const d = decorated[0]!;
    if (d.versionStatus === 'supported') {
      out.push(item);
      continue;
    }
    out.push(applyDecorationToMonacoItem(item, d, monaco));
  }

  return out;
}

function isCatalogEligible(
  item: any,
  keywordKind: number | undefined,
  functionKind: number | undefined,
  methodKind: number | undefined,
): boolean {
  if (item?.kind === keywordKind) return true;
  if (item?.kind === functionKind) return true;
  if (item?.kind === methodKind) return true;
  // Defensive: legacy LSP items sometimes mis-tag kind but carry a stable
  // detail string. Same trick as the keyword-casing post-process.
  const detail = typeof item?.detail === 'string' ? item.detail : '';
  if (/sql (keyword|function)/i.test(detail)) return true;
  return false;
}

function readLabelText(item: any): string | undefined {
  if (typeof item?.label === 'string') return item.label;
  if (typeof item?.label?.label === 'string') return item.label.label;
  return undefined;
}

function applyDecorationToMonacoItem(
  item: any,
  d: ReturnType<typeof decorateCandidates>[number],
  monaco: any,
): any {
  const baseLabel = readLabelText(item) ?? '';
  const labelOut = d.badge
    ? { label: baseLabel, description: d.badge }
    : item.label;
  const detailOut = mergeDetail(item.detail, d.detailSuffix);
  const tagsOut =
    d.versionStatus === 'deprecated'
      ? [monaco?.languages?.CompletionItemTag?.Deprecated ?? 1]
      : item.tags;
  const sortTextOut = bumpSortText(item.sortText, d.demote);
  return {
    ...item,
    label: labelOut,
    ...(detailOut !== undefined ? { detail: detailOut } : {}),
    ...(tagsOut ? { tags: tagsOut } : {}),
    ...(sortTextOut !== undefined ? { sortText: sortTextOut } : {}),
  };
}

function mergeDetail(detail: string | undefined, suffix: string | undefined): string | undefined {
  if (!suffix) return detail;
  if (!detail) return suffix;
  return `${detail} — ${suffix}`;
}

/**
 * Bump the leading single-digit tier in a sortText string by `demote`,
 * clamped to 9. Mirrors the ranker's `${effectiveTier}_${padded}` shape so
 * the demote stays sort-key-stable. If the format isn't recognized, returns
 * the original sortText unchanged so we never corrupt LSP-supplied keys.
 */
export function bumpSortText(sortText: string | undefined, demote: 0 | 1 | 2): string | undefined {
  if (!sortText || demote === 0) return sortText;
  const m = /^(\d)(.*)$/.exec(sortText);
  if (!m) return sortText;
  const tier = Math.min(Number(m[1]) + demote, 9);
  return `${tier}${m[2]}`;
}
